import { useMemo, useRef } from 'react';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { getAuthorization } from '@/utils/utils';
import { isEmpty } from 'lodash-es';
import { useChatList } from '@/pages/aigc/layouts/agentLayout/hooks';
import { useLocalRef } from './useLocalState';
import { useSessionId } from './useAgentModalSata';
import { processBatchChunks } from '../utils';
import { useDebounceEffect } from 'ahooks';
export interface NetworkRecoveryProps {
  mutate: (...args: any[]) => void;
  updateStreamingState: any;
  processStream: (...args: any[]) => void;
  resetStreamingState: (...args: any[]) => void;
}
const maxPollingCount = 20; // 最大轮询次数
const pollingInterval = 3000; // 轮询间隔 3 秒

// 集成网络恢复功能、SSE 控制器、网络状态、流式状态更新
const useNetworkRecovery = ({
  mutate,
  updateStreamingState,
  processStream,
  resetStreamingState,
  conversations,
  streamingState,
}: NetworkRecoveryProps) => {
  const { sessionId: routeSessionId } = useSessionId();
  const retryingRef = useLocalRef<boolean>(`__dba_chat_history_retrying_${routeSessionId}__`, false);
  const optionsRef = useLocalRef<{}>(`__dba_chat_history_catch_${routeSessionId}__`, {});
  const { fetchChatList } = useChatList({manual: true});  
  // 轮询相关状态
  const pollingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const pollingCountRef = useRef<number>(0);
  // 标记是否因超时而停止
  const isTimeoutRef = useRef<boolean>(false);
  // 用于判断断网重连之后决定是否再次重连
  const isConnectedRef = useRef<boolean>(false);
  // 用于记录后端返回的断网前的chunk条数
  // 这个待删除，设置动机：后端数据可能会返回多条list chunk数据，但是有时候只需要在收到数据的第一条做处理
  const chunkCountRef = useRef<number>(0);

  // 计算是否需要重新发起一个请求
  const isLastConversationPending = useMemo(() => {
    if (isEmpty(conversations)) return false;
    const lastConversation = conversations?.[conversations?.length - 1];
    if (
      isEmpty(lastConversation.answer) &&
      lastConversation.status !== 1 &&
      lastConversation.status !== ''
    ) {
      return lastConversation;
    }
    return false;
  }, [conversations]);

  useDebounceEffect(
    () => {
      if (isLastConversationPending && !streamingState) {
        setOptions({
          session_id: routeSessionId,
          message_id: isLastConversationPending?.messageId,
        });
        retrySSE();
      }
    },
    [streamingState, routeSessionId, isLastConversationPending?.messageId, isLastConversationPending],
    { wait: 200 },
  );

  // 清除轮询定时器
  const clearPollingTimer = () => {
    if (pollingTimerRef.current) {
      clearTimeout(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }
    pollingCountRef.current = 0;
    isTimeoutRef.current = false; // 重置超时标志
  };

  // 本地调试
  // const network = useNetwork();
  // useEffect(() => {
  //   if (!network.online) {
  //     handleOfflineEvent('networt erorr', 'pending');
  //   }
  // }, [network.online],);

  // 更新retrying状态
  const updateRetryingState = (state: boolean) => {
    retryingRef.current = state;
  };

  // 开始轮询
  const startPolling = (error?: string) => {
    // 清除之前的定时器
    clearPollingTimer();
    console.log('开始网络健康检查轮询');
    const poll = async () => {
      pollingCountRef.current += 1;
      console.log(`第 ${pollingCountRef.current} 次重连尝试`);

      // 检查是否超过最大轮询次数
      if (pollingCountRef.current > maxPollingCount) {
        console.log('已达到最大重连次数，停止重连');
        clearPollingTimer();
        // 更新流式状态为失败
        updateStreamingState('rejected');
        // 超出最大上限时，展示报错信息
        updateRetryingState(false);
        handleLimitEvent(error);
        return;
      }
      // 如果是已连接但超时导致的失败，不再继续重连
      if (isTimeoutRef.current) {
        console.log('已连接但请求超时，停止重连');
        clearPollingTimer();
        return;
      }

      try {
        // 尝试重连
        await retrySSE();
        // 如果执行到这里说明重连成功，停止轮询
        console.log('重连成功，停止轮询');
        clearPollingTimer();
      } catch (error) {
        // 重连失败，继续轮询
        console.log(
          `第 ${pollingCountRef.current} 次重连失败，${
            pollingInterval / 1000
          }秒后继续尝试`,
        );
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        pollingTimerRef.current = setTimeout(poll, pollingInterval);
      }
    };

    // 立即执行第一次轮询
    poll();
  };

  // 网络重试SSE
  const retrySSE = async () => {
    console.log('开始重试 SSE 连接');
    return new Promise<void>((resolve, reject) => {
      // 创建 AbortController 用于超时控制
      const abortController = new AbortController();
      const timeoutId = setTimeout(() => {
        console.log('SSE 连接超时（30秒），停止重连');
        // 设置超时标志
        isTimeoutRef.current = true;
        abortController.abort();
        // 更新流式状态为失败
        updateStreamingState('rejected');
        updateRetryingState(false);
        mutate((prevConversations) => [...prevConversations]);
        // reject(new Error('SSE 连接超时（30秒）'));
      }, 50000); // 50秒超时

      // 发起 SSE 连接
      fetchEventSource('/api/agent/continue', {

        method: 'POST', // 支持 POST
        openWhenHidden: true, // 强制保持连接
        signal: abortController.signal, // 添加超时信号
        headers: {
          Authorization: getAuthorization() || '', // 自定义请求头
        },
        body: JSON.stringify({
          session_id: optionsRef?.current?.session_id,
          message_id: optionsRef?.current?.message_id,
        }), // 请求体

        onopen: async (response) => {
          if (response.ok) {
            resetStreamingState();
            // 连接成功，清除超时定时器
            clearTimeout(timeoutId);
            // 连接成功，立即停止轮询
            clearPollingTimer();
            // // 网络恢复，触发成功回调
            handleOnlineEvent();
            isConnectedRef.current = true;
        
            console.log('连接成功', response.status);
            resolve(); // 连接成功，resolve Promise
          } else {
            // 连接失败，清除超时定时器
            clearTimeout(timeoutId);
            console.log('连接失败', response.status);
            reject(new Error(`连接失败: ${response.status}`));
          }
        },
        onmessage(rawChunk) {
          // 如果是批量chunk数据，优先外部处理，在处理单条chunk数据
          if (typeof rawChunk?.data === 'string' && rawChunk?.data.trim().startsWith('[') && rawChunk?.data.trim().endsWith(']')) {
            chunkCountRef.current += 1;
            processBatchChunks(rawChunk.data, optionsRef?.current?.message_id, processStream, resetAnswer);
          } else {
            processStream(rawChunk, optionsRef?.current?.message_id);
          }
          // 调用保存的 onSuccess 回调
          optionsRef?.current?.onSuccess?.();
        },

        onclose() {
          console.log('重试连接关闭');
          // 连接关闭，清除超时定时器
          clearTimeout(timeoutId);
          //  更新流式状态为完成
          updateStreamingState('complete');
          // 执行外部传入的onComplete回调函数
          optionsRef?.current?.onComplete?.();
          fetchChatList();
        },

        onerror(error) {
          console.error('重试连接发生错误:', error);
          console.log('error:~onError', error);
          // 发生错误，清除超时定时器
          clearTimeout(timeoutId);
          isConnectedRef.current && handleOfflineEvent(error?.message, 'rejected');
          // 调用保存的 onError 回调
          optionsRef?.current?.onError?.(error);
          reject(error);
          // 抛出错误以阻止 fetchEventSource 的自动重试机制
          throw error;
        },
      }).catch((error) => {
        // 捕获 fetchEventSource 的错误
        reject(error);
      });
    });
  };

  // 断网前的事件处理方法
  const handleOfflineEvent = (error?: any, errorStateType?: string) => {
    if (error && (errorStateType !== 'complete' || !errorStateType)) {
      updateRetryingState(true);
      // 重置轮询计数器和超时标志，开始新的重连尝试
      pollingCountRef.current = 0;
      isTimeoutRef.current = false;
      isConnectedRef.current = false;
      chunkCountRef.current = 0;
      // 开始轮询检查网络状态
      startPolling(error);
      mutate((prevConversations) => [...prevConversations]);
    }
  };

  // 轮询二十次上限提示报错
  const handleLimitEvent = (error: string | undefined) => {
    mutate((prevConversations) => {
      // 如果之前没有对话记录或为空数组，直接返回原数据
      if (!prevConversations || prevConversations?.length === 0) {
        return prevConversations;
      }
      // 获取最后一条对话记录
      const lastConversation =
          prevConversations[prevConversations.length - 1];
        // 更新最后一条回答的状态：断网情况下，不确定是哪一条，直接更新最后一条
      const updatedAnswer = lastConversation?.answer;
      // 防止重复添加错误信息
      const isErrorFlag = updatedAnswer?.filter(
        (task) => task?.type === 'error' && task?.value?.code === error,
      );
      isEmpty(isErrorFlag) &&
          updatedAnswer.push({
            type: 'error',
            value: {
              code: error,
              errorMsg: error,
            },
            flag: 'errorRefresh',
          });
      // 返回更新后的对话数组：保留除最后一条外的所有对话，更新最后一条对话的回答内容
      return [
        ...prevConversations.slice(0, -1),
        { ...lastConversation, answer: updatedAnswer },
      ];
    });
  };

  // 重置answer数据
  const resetAnswer = () => {
    if (chunkCountRef.current === 1) {
      // 清空当前对话的answer，准备重新填充
      mutate((prevConversations) => {
        if (!prevConversations || prevConversations.length === 0) {
          return prevConversations;
        }
        const lastConversation = prevConversations[prevConversations.length - 1];
        if (lastConversation?.messageId) {
        // 重置最后一条对话的answer
          lastConversation.answer = [];
        }
        return [...prevConversations];
      });
    }
  };
  // 断网恢复后的事件处理方法
  const handleOnlineEvent = () => {
    updateRetryingState(false);
    mutate((prevConversations) => [...prevConversations]);
  };

  // 设置 options 的方法
  const setOptions = (options: {
    onSuccess?: () => void;
    onError?: (error?: any) => void;
    onComplete?: () => void;
    session_id?: string;
    message_id?: string;
  }) => {
    optionsRef.current = options;
  };

  return {
    retrySSE,
    handleOfflineEvent,
    setOptions, // 暴露设置方法
    retrying: retryingRef.current,
  };
};

export default useNetworkRecovery;
